import { Maybe } from "true-myth";
import { eqBy, find, indexBy, prop, propEq, unionWith, without } from "ramda";

import {
  SchedulingActionTypes,
  SchedulingTypeKeys,
  MODAL_STATUS,
} from "actions/scheduling";
import { BlockActionTypes, BlockTypeKeys } from "actions/scheduling/blocks";

import {
  addOrUpdateShift,
  deleteShiftFromSchedule,
} from "helpers/schedule/shifts";
import {
  mergeManySchedules,
  EMPTY_SCHEDULE,
} from "helpers/transforms/schedule";
import { ISchedulingState } from "types/reducers";
import { RequestStatus } from "types/requests";
import { IPosUser } from "types/schedules";

const initialState: ISchedulingState = {
  schedule: EMPTY_SCHEDULE,
  autoSaveIntervalId: 0,
  draft: Maybe.nothing(),
  published: Maybe.nothing(),
  unavailBlocks: [],
  users: {},
  requestStatuses: {
    draftScheduleGet: RequestStatus.NOT_STARTED,
    draftSchedulePost: RequestStatus.NOT_STARTED,
    notifySchedulePost: RequestStatus.NOT_STARTED,
    posUsersGet: RequestStatus.NOT_STARTED,
    publishedScheduleGet: RequestStatus.NOT_STARTED,
    publishSchedulePost: RequestStatus.NOT_STARTED,
    unavailDelete: RequestStatus.NOT_STARTED,
    unavailGet: RequestStatus.NOT_STARTED,
    unavailPost: RequestStatus.NOT_STARTED,
  },
  showNotifyScheduleModal: MODAL_STATUS.HIDE,
  timeBlockModalData: Maybe.nothing(),
};

export default function scheduling(
  state: ISchedulingState = initialState,
  action: SchedulingActionTypes | BlockActionTypes
): ISchedulingState {
  switch (action.type) {
    case SchedulingTypeKeys.SET_SCHEDULE_DATE_BOUNDARIES:
      return {
        ...state,
        schedule: {
          ...state.schedule,
          startDate: Maybe.of(action.startDate),
          endDate: Maybe.of(action.endDate),
        },
      };

    case SchedulingTypeKeys.RECEIVE_SAVED_SCHEDULE: {
      const { savedSchedule, options } = action;
      let baseSchedule = state.schedule;
      if (options.overwrite) {
        // If instructed to overwrite, discard current people[]
        baseSchedule = {
          ...baseSchedule,
          people: [],
        };
      }

      const mergedSavedSchedule = mergeManySchedules(
        savedSchedule,
        baseSchedule
      );

      return {
        ...state,
        draft: Maybe.of(mergedSavedSchedule),
        schedule: mergedSavedSchedule,
      };
    }

    case SchedulingTypeKeys.RECEIVE_DRAFT_SCHEDULE: {
      const { draft } = action;
      return {
        ...state,
        draft: Maybe.of(draft),
      };
    }

    case SchedulingTypeKeys.SET_AUTOSAVE_INTERVAL_ID: {
      const { autoSaveIntervalId } = action;
      return {
        ...state,
        autoSaveIntervalId,
      };
    }

    case SchedulingTypeKeys.RECEIVE_PUBLISHED_SCHEDULE: {
      const { publishedSchedule } = action;
      const mergedPublishedSchedule = mergeManySchedules(publishedSchedule);

      return {
        ...state,
        published: Maybe.of(mergedPublishedSchedule),
      };
    }

    case SchedulingTypeKeys.RECEIVE_PUBLIC_PUBLISHED_SCHEDULE: {
      const { publishedSchedule } = action;
      const mergedPublishedSchedule = mergeManySchedules(publishedSchedule);

      return { ...state, schedule: mergedPublishedSchedule };
    }

    case SchedulingTypeKeys.RECEIVE_TEMPLATE_SCHEDULE: {
      const { templateSchedule } = action;
      return {
        ...state,
        schedule: templateSchedule,
      };
    }

    case SchedulingTypeKeys.RECEIVE_UNAVAILABILITY_BLOCKS: {
      const { blocks } = action;
      // Union the new blocks, replacing any existing by comparing uuid
      return {
        ...state,
        unavailBlocks: unionWith(
          // @ts-expect-error TODO please fix this error
          eqBy(prop("uuid")),
          blocks,
          state.unavailBlocks
        ),
      };
    }

    case SchedulingTypeKeys.REMOVE_UNAVAILABILITY_BLOCK: {
      const { uuid } = action;
      const blockToRemove = find(propEq("uuid", uuid), state.unavailBlocks);
      return {
        ...state,
        unavailBlocks: blockToRemove
          ? without([blockToRemove], state.unavailBlocks)
          : state.unavailBlocks,
      };
    }

    case SchedulingTypeKeys.RECEIVE_POS_USERS: {
      const { posUsers } = action;
      const users = indexBy<IPosUser>(prop("uuid"), posUsers);
      return {
        ...state,
        users,
      };
    }

    case SchedulingTypeKeys.SET_REQUEST_STATUS: {
      return {
        ...state,
        requestStatuses: {
          ...state.requestStatuses,
          [action.apiRequest]: action.status,
        },
      };
    }

    case SchedulingTypeKeys.TOGGLE_NOTIFY_MODAL: {
      return {
        ...state,
        showNotifyScheduleModal: action.modalAction,
      };
    }

    case BlockTypeKeys.SET_BLOCK_MODAL_DATA: {
      return {
        ...state,
        timeBlockModalData: Maybe.of(action.data),
      };
    }

    case BlockTypeKeys.SAVE_SHIFT: {
      const { shift, existingAssignee, existingShift } = action;
      return {
        ...state,
        schedule: addOrUpdateShift(
          shift,
          existingShift,
          existingAssignee,
          state.schedule
        ),
      };
    }

    case BlockTypeKeys.DELETE_SHIFT: {
      const { shift, assignee } = action;
      return {
        ...state,
        schedule: deleteShiftFromSchedule(shift, assignee, state.schedule),
      };
    }

    default: {
      return state;
    }
  }
}
